import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, Typography, Chip } from '@mui/material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
// hooks
import useSettings from '../../../hooks/useSettings';
import useAuth from '../../../hooks/useAuth';
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

function formatCountdown(ms) {
  if (ms <= 0) return 'Muddat tugadi!';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const isDesktop = useResponsive('up', 'lg');
  const { themeMode, onToggleMode } = useSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [urgentEssay, setUrgentEssay] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  const fetchUrgent = useCallback(async () => {
    if (user?.role !== 'EXPERT') return;
    try {
      const { data } = await axios.get('/essays/expert/counts');
      setUrgentEssay(data.urgentEssay);
    } catch {
      // ignore
    }
  }, [user?.role]);

  useEffect(() => {
    fetchUrgent();
    const interval = setInterval(fetchUrgent, 30000);
    return () => clearInterval(interval);
  }, [fetchUrgent]);

  // Sahifa o'zgarganda ham yangilash
  useEffect(() => { fetchUrgent(); }, [pathname, fetchUrgent]);

  // Countdown timer — har soniyada
  useEffect(() => {
    if (!urgentEssay?.deadline) {
      setCountdown('');
      return;
    }

    const tick = () => {
      const remaining = new Date(urgentEssay.deadline).getTime() - Date.now();
      setCountdown(formatCountdown(remaining));
      setIsExpired(remaining <= 0);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [urgentEssay]);

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}

        <Searchbar />
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {/* Countdown timer */}
          {urgentEssay && countdown && (
            <Chip
              icon={<Iconify icon="mdi:clock-alert-outline" width={18} />}
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>
                    {countdown}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85, display: { xs: 'none', sm: 'inline' } }}>
                    — #{urgentEssay.id}
                  </Typography>
                </Stack>
              }
              onClick={() => navigate(`/review/${urgentEssay.id}`)}
              sx={{
                bgcolor: isExpired ? 'error.main' : 'error.dark',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                height: 36,
                px: 1,
                '& .MuiChip-icon': { color: '#fff' },
                animation: isExpired ? 'pulse 1s infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                },
                '&:hover': { bgcolor: 'error.darker' },
              }}
            />
          )}

          <IconButtonAnimate onClick={onToggleMode} sx={{ color: 'text.primary' }}>
            <Iconify icon={themeMode === 'light' ? 'ph:sun-duotone' : 'ph:moon-duotone'} width={22} />
          </IconButtonAnimate>
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
